import React from 'react';
import { X } from 'lucide-react';
import { Badge } from 'src/shared/components/ui/badge';
import { Button } from 'src/shared/components/ui/button';
import type { SelectedFilters } from 'src/app/portfolio/state/portfolioGeneralSlice';
import type { SiteFilterCountry, SiteFilterIsicCode } from 'src/app/portfolio/state/api/siteFilterOptionsQuery';
import { RISK_LEVELS, SON_LEVELS } from './RiskDimensionAccordion';

const FILTER_NAME_LABELS: Record<string, string> = {
    isic: 'Activity',
    country: 'Country',
    physical_risk_levels: 'Physical Risk',
    dependency_levels: 'Dependencies',
    impact_levels: 'Impact on Nature',
    son_levels: 'State of Nature',
    kba: 'KBA',
};

const KBA_VALUE_LABELS: Record<string, string> = {
    within_boundary: 'Within site boundary',
    within_buffer: 'Within site buffer zone',
    no_overlap: 'No overlap',
};

const RISK_VALUE_LABELS: Record<string, string> = Object.fromEntries([...RISK_LEVELS, ...SON_LEVELS].map(l => [l.value, l.label]));
const MAX_BADGE_LABEL_LENGTH = 25;

const truncateLabel = (value: string, maxLength = MAX_BADGE_LABEL_LENGTH) => {
    if (value.length <= maxLength) return value;
    return `${value.slice(0, maxLength - 1)}…`;
};

const resolveValueLabel = (name: string, value: string): string => {
    if (name === 'kba') return KBA_VALUE_LABELS[value] ?? value;
    return RISK_VALUE_LABELS[value] ?? value;
};

/** Where the bar is drawn. The site list scrolls under it, so there it sticks to
 *  the top; the map fills its pane and does not scroll, so there it floats over it. */
type Layout = 'inline' | 'overlay';

const LAYOUT_CLASSES: Record<Layout, string> = {
    inline: 'sticky top-0 z-10 -mx-8 bg-grey-0 px-8 py-2',
    overlay:
        'absolute left-3 top-4 z-10 max-w-[calc(100%-2rem)] rounded-lg outline outline-1 outline-grey-20 bg-white/95 px-3 py-2 shadow-sm',
};

/** One filter the reader has set, and the × that takes it off. */
const RemovableBadge = ({ label, onRemove, truncate = true }: { label: string; onRemove?: () => void; truncate?: boolean }) => (
    <Badge variant='outline' className='flex items-center bg-white gap-1'>
        <span title={label}>{truncate ? truncateLabel(label) : label}</span>
        <button
            type='button'
            aria-label={`Remove ${label} filter`}
            onClick={onRemove}
            className='inline-flex items-center justify-center text-muted-foreground hover:text-black'
        >
            <X className='h-3.5 w-3.5' />
        </button>
    </Badge>
);

type Props = {
    selectedFilters: SelectedFilters;
    countries?: SiteFilterCountry[];
    isicCodes?: SiteFilterIsicCode[];
    onRemove: (name: string, values: string[]) => void;
    onClearAll: () => void;
    matchingSiteCount?: number;
    selectedPeriodLabel?: string | null;
    onClearPeriod?: () => void;
    /** The term in the search box. The map carries no search box, so this pill is
     *  the only thing that says why a site is missing from it. */
    searchTerm?: string;
    onClearSearch?: () => void;
    layout?: Layout;
};

const GnrActiveFilters = ({
    selectedFilters,
    countries = [],
    isicCodes = [],
    onRemove,
    onClearAll,
    matchingSiteCount,
    selectedPeriodLabel,
    onClearPeriod,
    searchTerm,
    onClearSearch,
    layout = 'inline',
}: Props) => {
    const totalValues = selectedFilters.reduce((sum, f) => sum + f.values.length, 0);
    const totalBadges = totalValues + (selectedPeriodLabel ? 1 : 0) + (searchTerm ? 1 : 0);
    const countryNameByCode = new Map(countries.map(country => [country.code, country.name]));
    const isicLabelByCode = new Map(isicCodes.map(isicCode => [isicCode.code, `[${isicCode.code}] ${isicCode.description}`]));

    if (totalBadges === 0) return null;

    return (
        <div className={`flex flex-wrap items-center gap-2 ${LAYOUT_CLASSES[layout]}`} data-testid='gnr-active-filters'>
            <span className='text-sm text-muted-foreground'>
                Active Filters
                {matchingSiteCount !== undefined
                    ? ` (${matchingSiteCount.toLocaleString()} ${matchingSiteCount === 1 ? 'site' : 'sites'})`
                    : ''}
                :
            </span>
            {/* Whole: a period label is short and fixed, and truncating it loses the year. */}
            {selectedPeriodLabel && (
                <RemovableBadge label={`Time Period: ${selectedPeriodLabel}`} onRemove={onClearPeriod} truncate={false} />
            )}
            {searchTerm && <RemovableBadge label={`Search: ${searchTerm}`} onRemove={onClearSearch} />}
            {selectedFilters.flatMap(filter =>
                filter.values.map(value => {
                    const nameLabel = FILTER_NAME_LABELS[filter.name] ?? filter.name;
                    const valueLabel =
                        filter.name === 'country'
                            ? countryNameByCode.get(value) ?? value
                            : filter.name === 'isic'
                            ? isicLabelByCode.get(value) ?? value
                            : resolveValueLabel(filter.name, value);
                    return (
                        <RemovableBadge
                            key={`${filter.name}-${value}`}
                            label={`${nameLabel}: ${valueLabel}`}
                            onRemove={() =>
                                onRemove(
                                    filter.name,
                                    filter.values.filter(v => v !== value)
                                )
                            }
                        />
                    );
                })
            )}
            {totalBadges >= 2 && (
                <Button type='button' variant='link' size='mini' onClick={onClearAll} className='text-muted-foreground'>
                    Clear
                </Button>
            )}
        </div>
    );
};

export default GnrActiveFilters;
